import React, { Suspense, useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar/Sidebar';
import Header from '@/components/Header/Header';
import MobileHeader from '@/components/Header/MobileHeader';
import QuickActions from '@/components/Header/QuickActions';
import BottomNav, { BOTTOM_NAV_HEIGHT } from '@/components/BottomNav/BottomNav';
import { ActionIcon, AppShell, Box, Burger, Button, Center, Drawer, Group, LoadingOverlay, Stack, Text, Tooltip } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import logoBlue from '@/assets/logogovex_white.png';
import { RouteUtils } from '@bobex/react-core/routes/RouteUtils';
import {
  IconBell,
  IconCalendar,
  IconCurrencyEuro,
  IconDialpad,
  IconMessage,
  IconSearch,
  IconSeparator,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { AccountService } from '@bobex/react-core/services/AccountService';
import { useBoLoginInit } from '@bobex/react-core/utils/hooks/useBoLoginInit';
import LoadingScreen from '@bobex/react-core/components/LoadingScreen/LoadingScreen';
import {LanguagePicker} from '@bobex/react-core/components/LanguagePicker/LanguagePicker';
import {ColorSchemeToggle} from '@bobex/react-core/components/ColorSchemeToggle/ColorSchemeToggle';
import {Outlet, useLocation} from 'react-router-dom';
import RightDrawer from '@/components/SearchHeader/RightDrawer';
import UserMenu from '@/components/UserMenu/UserMenu';

export default function AppLayout() {
  const { t } = useTranslation();
  const user = AccountService.getUser();
  const { pathname } = useLocation();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState<'search' | 'cpv'>('search');

  const { i18n } = useTranslation();
  const locale = i18n.language.replace('_', '-');

  // Close the mobile navigation drawer after navigating to another React route
  useEffect(() => {
    closeMobile();
  }, [pathname, closeMobile]);

  // Legacy -> React login handshake (one-shot ?token=...), see useBoLoginInit
  const { isLoading, failed: loginFailed, retry: retryLogin } = useBoLoginInit();
  useHotkeys([
    [
      'mod+K',
      () => {
        setRightPanelTab('search');
        setIsDrawerOpen((v) => !v);
      },
    ],
  ]);

  if (loginFailed) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="sm">
          <Text fw={600}>{t('login.boLoginFailedTitle', 'Could not sign you in automatically')}</Text>
          <Text size="sm" c="dimmed">
            {t('login.boLoginFailedText', 'The login handshake with the backoffice failed several times in a row.')}
          </Text>
          <Button onClick={retryLogin}>{t('login.boLoginRetry', 'Try again')}</Button>
        </Stack>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ type: 'bars' }}
      />
    );
  }

  return (
    <AppShell
      bg={'bodyBackgroundColor'}
      header={{
        height: { base: 60, lg: 64 },
      }}
      footer={{ height: { base: BOTTOM_NAV_HEIGHT, lg: 0 } }}
      layout={'alt'}
      navbar={{
        width: 250,
        breakpoint: 'lg',
        collapsed: { mobile: true, desktop: !desktopOpened },
      }}
    >
      <AppShell.Header>
        {/* Desktop (>=lg): everything fits on a single row, no wrap. */}
        <Group
          visibleFrom="lg"
          align="center"
          wrap="nowrap"
          h={64}
          gap={6}
          style={{
            padding: '0 0.6rem',
            zIndex: 1000,
            position: 'relative',
            overflowX: 'auto',
          }}
        >
          <Group wrap={'nowrap'} gap={'xs'}>
            <Burger
              opened={desktopOpened}
              onClick={toggleDesktop}
              size="sm"
              style={{ marginRight: '10px' }}
            />
            <Header />
          </Group>

          <Box style={{ flex: 1 }} />

          <Group wrap="nowrap" gap={'xs'}>
            <ColorSchemeToggle />
            <Box
              component="span"
              style={{ whiteSpace: 'nowrap', fontSize: 'var(--mantine-font-size-sm)' }}
            >
              {new Intl.DateTimeFormat(locale, {
                dateStyle: 'full',
                timeZone: 'UTC',
              }).format(new Date())}
            </Box>
            <LanguagePicker />
          </Group>

          <Box style={{ flex: 1 }} />

          <Group wrap="nowrap" gap={'xs'}>
            <Tooltip label={'ctrl+k'}>
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Search"
                onClick={() => {
                  setRightPanelTab('search');
                  setIsDrawerOpen((v) => !v);
                }}
              >
                <IconSearch stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <ActionIcon variant="default" size="lg" aria-label="Unpaid clients">
              <IconCurrencyEuro stroke={1.5} />
            </ActionIcon>
            <ActionIcon variant="default" size="lg" aria-label="notifications">
              <IconBell stroke={1.5} />
            </ActionIcon>
            <Tooltip label={t('Calendar')}>
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="Calendar"
                component="a"
                href={
                  user?.userID
                    ? RouteUtils.getBackofficeServletBasename() +
                      `/todo_calendar_call?userid=${user.userID}`
                    : '#'
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconCalendar stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <ActionIcon variant="default" size="lg" aria-label="notifications">
              <IconMessage stroke={1.5} />
            </ActionIcon>
            <UserMenu />
            <Tooltip label={t('CPV codes', 'CPV codes')}>
              <ActionIcon
                variant="default"
                size="lg"
                aria-label="CPV codes"
                onClick={() => {
                  setRightPanelTab('cpv');
                  setIsDrawerOpen(true);
                }}
              >
                <IconDialpad stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Mobile/tablet (<lg): compact top bar, navigation lives in a drawer + bottom bar. */}
        <MobileHeader
          menuOpened={mobileOpened}
          onToggleMenu={toggleMobile}
          onOpenCpv={() => {
            setRightPanelTab('cpv');
            setIsDrawerOpen(true);
          }}
        />
        <RightDrawer
          isVisible={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          activeTab={rightPanelTab}
        />
      </AppShell.Header>
      <AppShell.Navbar p="md" bg={'sidebarBackgroundColor'}>
        <AppShell.Section>
          <img src={logoBlue} alt="Govex" width={150} />
        </AppShell.Section>
        <AppShell.Section>
          <Sidebar />
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </AppShell.Main>

      {/* Mobile/tablet (<lg) navigation: bottom bar + drawer opened by the burger */}
      <BottomNav
        onOpenSearch={() => {
          setRightPanelTab('search');
          setIsDrawerOpen((v) => !v);
        }}
      />
      <Drawer
        opened={mobileOpened}
        onClose={closeMobile}
        size={280}
        padding={0}
        withCloseButton={false}
        hiddenFrom="lg"
        styles={{ body: { height: '100%' } }}
      >
        <Stack bg="sidebarBackgroundColor" h="100%" p="md" gap={0}>
          <img src={logoBlue} alt="Govex" width={130} style={{ marginBottom: 'var(--mantine-spacing-md)' }} />
          {/* Header actions that do not fit in the top bar on small screens */}
          <Group gap="xs" mb="md" hiddenFrom="sm">
            <QuickActions />
          </Group>
          <Box style={{ flex: 1, minHeight: 0 }}>
            <Sidebar height="100%" />
          </Box>
        </Stack>
      </Drawer>
    </AppShell>
  );
}

